import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Building2, Ruler, ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { LoadingSpinner } from '../../ui/LoadingSpinner';
import ErrorMessage from '../../ui/ErrorMessage';
import { calculationService } from '../../../services/calculationService';
import { DetailedEstimationData, EstimationResult } from '../../../types/estimation-items';
import { TVABreakdownPanel } from '../TVABreakdownPanel';
import { versionDetector } from '../../../services/estimationVersionDetector';
import { legacyAdapter } from '../../../services/legacyEstimationAdapter';

const PROPERTY_TYPE_TRANSLATIONS = {
  'house': 'Maison',
  'apartment': 'Appartement',
  'building': 'Immeuble'
} as const;

interface DetailedEstimateViewProps {
  data: DetailedEstimationData;
  onBack: () => void;
  estimateId?: string;
}

export const DetailedEstimateView: React.FC<DetailedEstimateViewProps> = ({ data, onBack, estimateId }) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [estimationResult, setEstimationResult] = useState<EstimationResult | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [adaptationWarning, setAdaptationWarning] = useState<{
    originalTotal: number;
    newTotal: number;
    difference: number;
    percentageDiff: number;
  } | null>(null);

  useEffect(() => {
    const calculateEstimate = async () => {
      try {
        setLoading(true);

        let processedData = data;
        let originalTotal: number | null = null;

        if (versionDetector.isLegacyFormat(data)) {
          console.log('🔄 Legacy estimation format detected, adapting...');

          if ((data as any).priceRange?.max) {
            originalTotal = (data as any).priceRange.max;
            console.log(`Original total from priceRange: ${originalTotal} €`);
          }

          processedData = legacyAdapter.adaptLegacyEstimation(data);
          console.log('✅ Estimation adapted to current format');

          const stats = legacyAdapter.getAdaptationStats();
          console.log('Adaptation stats:', stats);
        }

        const departmentCode = processedData.description?.location?.postcode?.substring(0, 2);
        const discounts = processedData.discounts || data.discounts;
        const result = calculationService.calculateEstimate(processedData, departmentCode, discounts);
        setEstimationResult(result);

        if (originalTotal && result.totalTTC) {
          const difference = result.totalTTC - originalTotal;
          const percentageDiff = Math.abs((difference / originalTotal) * 100);

          if (percentageDiff > 5) {
            setAdaptationWarning({
              originalTotal,
              newTotal: result.totalTTC,
              difference,
              percentageDiff
            });
            console.warn(`⚠️ Significant difference detected: ${percentageDiff.toFixed(1)}%`);
            console.warn(`Original: ${originalTotal} € -> New: ${result.totalTTC} €`);
          } else {
            console.log(`✅ Totals match within acceptable range (${percentageDiff.toFixed(1)}% difference)`);
          }
        }
      } catch (err) {
        console.error('Erreur lors du calcul:', err);
        setError('Impossible de calculer l\'estimation');
      } finally {
        setLoading(false);
      }
    };

    calculateEstimate();
  }, [data]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    if (estimationResult) {
      setExpandedCategories(new Set(estimationResult.categories.map(c => c.categoryId)));
    }
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
  };

  const handleExport = () => {
    if (!estimationResult) return;

    const doc = new jsPDF();

    const primaryColor = PDF_COLORS.primary;
    const secondaryColor = PDF_COLORS.secondary;
    const highlightColor = PDF_COLORS.highlight;
    const backgroundGray = PDF_COLORS.backgroundGray;

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text('SimulReno', 15, 18);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('ESTIMATION BUDGÉTAIRE DE TRAVAUX', 15, 25);

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text(`Date : ${new Date().toLocaleDateString('fr-FR')}`, 150, 18);
    doc.text(`ESTIMATION N° ${new Date().getFullYear()}-${Math.floor(Math.random() * 10000)}`, 150, 25);

    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text('Informations du bien', 15, 40);
    doc.setFontSize(11);
    doc.setTextColor(...secondaryColor);
    const infoY = 48;
    doc.text(`Adresse : ${data?.description?.location?.address || 'Non renseigné'}`, 15, infoY);
    doc.text(`Code postal : ${data?.description?.location?.postcode || 'N/A'} ${data?.description?.location?.city || ''}`, 15, infoY + 6);

    const translatedPropertyType = data?.description?.propertyType
      ? PROPERTY_TYPE_TRANSLATIONS[data.description.propertyType as keyof typeof PROPERTY_TYPE_TRANSLATIONS]
      : 'Non renseigné';

    doc.text(`Type : ${translatedPropertyType}`, 15, infoY + 12);
    doc.text(`Surface : ${data?.description?.area ? `${data.description.area} m²` : 'Non renseigné'}`, 15, infoY + 18);

    doc.setDrawColor(220, 220, 220);
    doc.line(15, 70, 195, 70);

    let yPosition = 80;

    estimationResult.categories.forEach(category => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFillColor(...primaryColor);
      doc.setTextColor(255, 255, 255);
      doc.rect(15, yPosition, 180, 8, 'F');
      doc.setFontSize(11);
      doc.text(category.categoryTitle.toUpperCase(), 20, yPosition + 5);
      yPosition += 12;

      const tableData: any[] = [];

      category.items.forEach(item => {
        const itemData = estimationDataService.getItem(item.category, item.itemKey);

        tableData.push([
          { content: item.name, styles: { fontStyle: 'bold', fontSize: PDF_FONT_SIZES.itemName, textColor: [0, 0, 0] } },
          `${item.quantity} ${item.unit}`,
          formatPriceForPDF(item.unitPrice),
          `${item.tvaRate}%`,
          formatPriceForPDF(item.totalPrice),
          formatPriceForPDF(item.totalTTC)
        ]);

        if (itemData?.details) {
          const details = itemData.details;
          const detailsContent: string[] = [];

          if (details.materials && details.materials.length > 0) {
            detailsContent.push(`Matériaux: ${details.materials.join(', ')}`);
          }

          if (details.workIncluded && details.workIncluded.length > 0) {
            detailsContent.push(`Travaux: ${details.workIncluded.join(', ')}`);
          }

          if (details.performance) detailsContent.push(`Performance: ${details.performance}`);
          if (details.warranty) detailsContent.push(`Garantie: ${details.warranty}`);
          if (details.note) detailsContent.push(`Note: ${details.note}`);

          if (detailsContent.length > 0) {
            tableData.push([
              {
                content: detailsContent.join(' | '),
                colSpan: 6,
                styles: {
                  fontSize: PDF_FONT_SIZES.tiny,
                  textColor: [100, 100, 100],
                  fontStyle: 'italic',
                  cellPadding: { top: 1, bottom: 3, left: 3, right: 3 },
                  fillColor: [252, 252, 252],
                  lineWidth: 0
                }
              }
            ]);
          }
        }
      });

      if (category.discountPercentage && category.discountAmount) {
        tableData.push([
          {
            content: `Décote volume (${category.discountPercentage}%)`,
            colSpan: 4,
            styles: {
              fontStyle: 'italic',
              fontSize: PDF_FONT_SIZES.small,
              textColor: [180, 0, 0],
              halign: 'right'
            }
          },
          {
            content: formatPriceForPDF(-category.discountAmount),
            styles: { textColor: [180, 0, 0], fontStyle: 'bold' }
          },
          ''
        ]);
      }

      doc.autoTable({
        startY: yPosition,
        head: [['Désignation', 'Qte', 'PU HT', 'TVA', 'Total HT', 'Total TTC']],
        body: tableData,
        margin: { left: 15, right: 15 },
        styles: {
          fontSize: PDF_FONT_SIZES.small,
          cellPadding: { top: 2, bottom: 2, left: 2, right: 2 },
          lineWidth: 0.1,
          lineColor: [230, 230, 230],
          overflow: 'linebreak',
          cellWidth: 'wrap'
        },
        headStyles: {
          fillColor: [245, 245, 245],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: PDF_FONT_SIZES.small
        },
        columnStyles: {
          0: { cellWidth: 50, overflow: 'linebreak', cellPadding: { top: 3, bottom: 3, left: 2, right: 2 } },
          1: { cellWidth: 16, halign: 'center' },
          2: { cellWidth: 30, halign: 'right' },
          3: { cellWidth: 12, halign: 'center' },
          4: { cellWidth: 30, halign: 'right' },
          5: { cellWidth: 32, halign: 'right', fontStyle: 'bold' }
        },
        tableWidth: 170
      });

      yPosition = (doc as any).lastAutoTable.finalY + 10;

      doc.setFontSize(PDF_FONT_SIZES.heading);
      doc.setTextColor(...primaryColor);
      doc.setFont(undefined, 'bold');
      doc.text(`Total ${category.categoryTitle} (HT): ${formatPriceForPDF(category.subtotal)}`, 20, yPosition);
      yPosition += 20;
    });

    const tvaBoxHeight = 15 + (estimationResult.tvaBreakdown.length * 12);
    const totalBoxHeight = 35;
    const requiredSpace = tvaBoxHeight + totalBoxHeight + 30;

    if (yPosition + requiredSpace > 280) {
      doc.addPage();
      yPosition = 20;
    }

    yPosition += 10;

    doc.setFillColor(...primaryColor);
    doc.setTextColor(255, 255, 255);
    doc.rect(15, yPosition, 180, 8, 'F');
    doc.setFontSize(11);
    doc.text('DÉTAIL DE LA TVA', 20, yPosition + 5);
    yPosition += 12;

    estimationResult.tvaBreakdown.forEach(tvaItem => {
      doc.setFillColor(...backgroundGray);
      doc.rect(15, yPosition, 180, 10, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.rect(15, yPosition, 180, 10);

      doc.setFontSize(8.5);
      doc.setTextColor(...secondaryColor);
      doc.setFont(undefined, 'bold');
      doc.text(`TVA à ${tvaItem.rate}%`, 20, yPosition + 6);

      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      const baseHTText = `Base HT : ${formatPriceForPDF(tvaItem.baseHT)}`;
      const tvaText = `TVA : ${formatPriceForPDF(tvaItem.tvaAmount)}`;
      const ttcText = `TTC : ${formatPriceForPDF(tvaItem.totalTTC)}`;

      doc.text(baseHTText, 60, yPosition + 6);
      doc.text(tvaText, 115, yPosition + 6);

      doc.setFont(undefined, 'bold');
      doc.text(ttcText, 160, yPosition + 6);

      yPosition += 12;
    });

    yPosition += 5;

    const boxX = 110;
    const boxY = yPosition;
    const boxWidth = 85;

    doc.setFillColor(...backgroundGray);
    doc.rect(boxX, boxY, boxWidth, totalBoxHeight, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.rect(boxX, boxY, boxWidth, totalBoxHeight);

    doc.setFontSize(11);
    doc.setTextColor(...primaryColor);
    doc.setFont(undefined, 'bold');
    doc.text('Récapitulatif financier', boxX + 5, boxY + 7);

    doc.setFontSize(9.5);
    doc.setTextColor(...secondaryColor);
    doc.setFont(undefined, 'normal');
    doc.text('Montant total HT :', boxX + 5, boxY + 15);
    doc.setFont(undefined, 'bold');
    doc.text(formatPriceForPDF(estimationResult.totalHT), boxX + boxWidth - 5, boxY + 15, { align: 'right' });

    doc.setFont(undefined, 'italic');
    doc.setFontSize(9);
    doc.text('TVA totale :', boxX + 5, boxY + 22);
    doc.setFont(undefined, 'normal');
    doc.text(formatPriceForPDF(estimationResult.tva), boxX + boxWidth - 5, boxY + 22, { align: 'right' });

    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...highlightColor);
    doc.text('Montant total TTC :', boxX + 5, boxY + 30);
    doc.text(formatPriceForPDF(estimationResult.totalTTC), boxX + boxWidth - 5, boxY + 30, { align: 'right' });

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i}/${pageCount}`, 105, 290, { align: 'center' });
      doc.text('SimulReno - Estimation détaillée des travaux', 15, 290);
    }

    doc.save(`estimation-detaillee-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const cardStyle: React.CSSProperties = {
    background: 'white',
    border: '0.5px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-card)',
    padding: '18px 20px',
  };

  const handleEdit = () => {
    localStorage.setItem('editEstimate', JSON.stringify({
      ...(data as any),
      _editMode: true,
      _estimateId: estimateId,
    }));
    navigate(`/estimation/${estimateId}/edit`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !estimationResult) {
    return <ErrorMessage message={error || "Impossible de calculer l'estimation"} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {adaptationWarning && (
        <div style={{ background: '#FFF7ED', borderLeft: '3px solid #FB923C', padding: '12px 14px', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0' }}>
          <p style={{ fontSize: 12, fontWeight: 500, color: '#9A3412', marginBottom: 4 }}>
            Différence détectée lors de l'adaptation
          </p>
          <p style={{ fontSize: 12, color: '#C2410C' }}>
            Le montant recalculé ({calculationService.formatPrice(adaptationWarning.newTotal)}) diffère de{' '}
            <strong>{adaptationWarning.percentageDiff.toFixed(1)}%</strong>{' '}
            du montant original ({calculationService.formatPrice(adaptationWarning.originalTotal)}).
            Cette différence peut être due à des mises à jour de prix. Le nouveau calcul utilise les prix et règles de TVA actuels.
          </p>
        </div>
      )}

      {/* Informations du bien */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: 12 }}>
          Informations du bien
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            <MapPin size={13} color="var(--color-text-secondary)" style={{ marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {data?.description?.location?.address || 'Non renseigné'}
              </div>
              {(data?.description?.location?.postcode || data?.description?.location?.city) && (
                <div style={{ fontSize: 12, color: '#7C6FA0', marginTop: 2 }}>
                  {[data?.description?.location?.postcode, data?.description?.location?.city].filter(Boolean).join(' ')}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Building2 size={13} color="var(--color-text-secondary)" />
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              {data?.description?.propertyType
                ? PROPERTY_TYPE_TRANSLATIONS[data.description.propertyType as keyof typeof PROPERTY_TYPE_TRANSLATIONS]
                : 'Non renseigné'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Ruler size={13} color="var(--color-text-secondary)" />
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              {data?.description?.area ? `${data.description.area} m²` : 'Non renseigné'}
            </span>
          </div>
        </div>
      </div>

      {/* Estimation totale */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 15, fontWeight: 500, color: 'var(--color-primary)', marginBottom: 14 }}>
          Estimation totale
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Total HT</span>
            <span style={{ fontSize: 13, color: 'var(--color-text-primary)', fontWeight: 500 }}>
              {calculationService.formatPrice(estimationResult.totalHT)}
            </span>
          </div>
          {estimationResult.totalDiscountAmount && estimationResult.totalDiscountAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>dont décotes volume</span>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                − {calculationService.formatPrice(estimationResult.totalDiscountAmount)}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Total TVA</span>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              {calculationService.formatPrice(estimationResult.tva)}
            </span>
          </div>
          <div style={{ borderTop: '0.5px solid var(--color-border)', paddingTop: 10, marginTop: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 15, fontWeight: 500, color: '#1E1148' }}>Total TTC</span>
            <span style={{ fontSize: 18, fontWeight: 500, color: '#1E1148' }}>
              {calculationService.formatPrice(estimationResult.totalTTC)}
            </span>
          </div>
        </div>
        <p style={{ fontSize: 11, color: '#7C6FA0', fontStyle: 'italic', marginTop: 14 }}>
          Les prix dans SimulReno sont basés sur le marché à début 2026. Ils sont volontairement surévalués d'environ 10%. Il est toujours préférable de provisionner une marge pour les aléas lorsque l'on se lance dans une rénovation.
        </p>
      </div>

      {/* Détail par catégorie */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>
            Détail par catégorie
          </h3>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={expandAll} style={{ fontSize: 12, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              Tout développer
            </button>
            <button onClick={collapseAll} style={{ fontSize: 12, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              Tout réduire
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {estimationResult.categories.map(category => (
            <div key={category.categoryId} style={{ border: '0.5px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
              <button
                onClick={() => toggleCategory(category.categoryId)}
                onMouseEnter={e => (e.currentTarget.style.background = '#F5F3FF')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 'var(--radius-sm)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {expandedCategories.has(category.categoryId)
                    ? <ChevronUp size={16} color="#7C6FA0" />
                    : <ChevronDown size={16} color="#7C6FA0" />}
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>{category.categoryTitle}</span>
                  <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>({category.items.length} éléments)</span>
                </div>
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>
                    {calculationService.formatPrice(category.subtotal)}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--color-text-secondary)', fontWeight: 400 }}>HT</span>
                </span>
              </button>

              {expandedCategories.has(category.categoryId) && (
                <div style={{ borderTop: '0.5px solid var(--color-border)', padding: '12px 12px', background: 'rgba(0,0,0,0.01)' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {category.items.map((item, idx) => (
                      <div key={item.itemKey} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: idx < category.items.length - 1 ? '0.5px solid var(--color-border)' : 'none' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>{item.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                            {item.quantity} {item.unit} × {calculationService.formatPrice(item.unitPrice)}
                          </div>
                          <div style={{ fontSize: 11, color: '#7C6FA0', marginTop: 1 }}>
                            TVA {item.tvaRate}% : {calculationService.formatPrice(item.tvaAmount)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>HT</div>
                          <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>
                            {calculationService.formatPrice(item.totalPrice)}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>TTC</div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-primary)' }}>
                            {calculationService.formatPrice(item.totalTTC)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {category.discountPercentage && category.discountAmount && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '0.5px solid var(--color-border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        <span>Décote volume ({category.discountPercentage}%)</span>
                        <span style={{ color: '#DC2626' }}>− {calculationService.formatPrice(category.discountAmount)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <TVABreakdownPanel
        tvaBreakdown={estimationResult.tvaBreakdown}
        totalHT={estimationResult.totalHT}
        totalTVA={estimationResult.tva}
        totalTTC={estimationResult.totalTTC}
        buildingAge={data.description?.buildingAge}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button
          onClick={onBack}
          style={{ fontSize: 12, color: 'var(--color-text-secondary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          ← Retour
        </button>
        {estimateId && (
          <button
            onClick={handleEdit}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '8px 16px', border: 'none', borderRadius: 'var(--radius-md)', background: 'var(--gradient-primary)', color: 'white', cursor: 'pointer' }}
          >
            <Pencil size={13} />
            Modifier cette estimation
          </button>
        )}
      </div>
    </div>
  );
};

export default DetailedEstimateView;
